// Phase 129 Plan 04 Task 1 — PostHog wrapper for web/.
//
// Two invariants this file enforces:
//
// 1. **cross_subdomain_cookie:true (Blocker 5 — Phase 113 stitch invariant).**
//    Setting this flag causes posthog-js to write the cookie with
//    Domain=.hookmyapp.com so it's readable on hookmyapp.com AND
//    app.hookmyapp.com. The existing Base44 marketing init at
//    hookmyapp-marketing/src/App.jsx (lines 22-34) does NOT set this flag —
//    the Phase 113 marketing→app distinct_id stitch was working by browser
//    default coincidence, not by configuration. Phase 129 closes the gap
//    explicitly. The Wave-0 Playwright test (homepage-demo.page.test.ts case
//    5) asserts `document.cookie` carries a PostHog cookie with
//    Domain=.hookmyapp.com after init runs.
//
// 2. **Typed events via @hookmyapp/observability EventRegistry.** The emit()
//    wrapper is generic over EventName so tsc rejects unknown event names and
//    mismatched property shapes at compile time. CONTEXT.md §16 baseline
//    properties (site, environment, workspace_id, days_since_signup) are
//    registered as super-properties on init so every capture carries them
//    without per-call duplication.
import posthog from 'posthog-js';
// Type-only imports erase at compile time — barrel is fine for `import type`
// since Node-only runtime code never enters the bundle. Runtime values come
// only from `posthog-js`.
import type {
  BaselineProperties,
  EventName,
  EventProperties,
} from '@hookmyapp/observability';

let initialized = false;
let isLocalDev = false;

export function initAnalytics(): void {
  if (typeof window === 'undefined' || initialized) return;
  // The init path always runs when a token is provided so the cross-subdomain
  // cookie is written (Phase 113 stitch invariant — Blocker 5). In local-dev the
  // api_host is pointed at an intentionally unreachable localhost URL so network
  // sends never leave the browser, while posthog.capture still runs its full
  // internal pipeline (including the `eventCaptured` internal event). This allows
  // Playwright tests to intercept events via `posthog.on('eventCaptured', ...)`.
  isLocalDev = import.meta.env.VITE_IS_LOCAL_DEV === '1';
  // Local-dev fallback token lets the cookie path execute without leaking real
  // captures (api_host is unreachable in local-dev anyway). Production/staging
  // always carry the real token.
  const token = import.meta.env.VITE_PUBLIC_POSTHOG_TOKEN || (isLocalDev ? 'phc_local_dev_placeholder' : '');
  if (!token) return;

  posthog.init(token, {
    // In local-dev, point to an unreachable endpoint so network sends fail
    // silently without polluting real PostHog projects. The capture pipeline
    // still runs fully (eventCaptured fires) so Playwright can intercept events.
    // Port 4999 is not used by any local service, so the TCP connection is
    // refused immediately — no unsafe-port browser rejection.
    api_host: isLocalDev
      ? 'http://localhost:4999'
      : (import.meta.env.VITE_PUBLIC_POSTHOG_HOST ?? 'https://us.i.posthog.com'),
    ui_host: 'https://us.posthog.com',
    // Cookie name intentionally NOT set: posthog-js's default
    // `ph_<token>_posthog` is identical between web/ and frontend/ when both
    // call init with the same project token, so the .hookmyapp.com cookie is
    // shared automatically. Earlier customizations to `cookie_name` here +
    // `ph_hookmyapp` on the app desynchronized the two surfaces (see Phase
    // 113-03 bug discovered 2026-05-10 via langtalks1 cohort: 62 anon
    // marketing visitors, 0 stitched to app signups).
    cross_subdomain_cookie: true,
    persistence: 'localStorage+cookie',
    defaults: '2026-01-30',
    person_profiles: 'always',
    // Never opt-out at init time — the unreachable api_host suppresses network
    // sends in local-dev instead. opt_out_capturing_by_default would prevent
    // posthog's internal eventCaptured hook from firing (used by Playwright tests).
    //
    // opt_out_useragent_filter: in local-dev (Playwright runs headless Chromium
    // which posthog detects as a bot), disable the bot filter so capture runs
    // through the full pipeline and eventCaptured fires for test assertions.
    opt_out_useragent_filter: isLocalDev,
    autocapture: !isLocalDev,
    capture_pageview: !isLocalDev,
    capture_performance: { web_vitals: !isLocalDev },
  });

  const env: BaselineProperties['environment'] = isLocalDev
    ? 'local'
    : import.meta.env.MODE === 'production'
      ? 'production'
      : 'staging';

  // Phase 113 — every event carries site + environment so the shared PostHog
  // project can split funnels by surface.
  posthog.register({
    site: 'web',
    environment: env,
    days_since_signup: null,
  } satisfies Pick<BaselineProperties, 'site' | 'environment' | 'days_since_signup'>);

  // Mirror posthog snippet-loader behaviour for DevTools/Playwright observation.
  (window as Window & { posthog?: typeof posthog }).posthog = posthog;
  initialized = true;
  emit('web_page_hydrated', {
    navigation_to_hydration_ms: Math.round(performance.now()),
    pathname: window.location.pathname,
  });
}

/**
 * Typed PostHog capture wrapper. Compile-time gate via EventName ensures every
 * event name is in the @hookmyapp/observability EventRegistry; the property
 * shape must match EventProperties<E>.
 *
 * Server-side cousin lives at @hookmyapp/observability emit() (node entry).
 *
 * In local-dev, posthog.init runs with `opt_out_capturing_by_default:true`, so
 * `posthog.capture` is a no-op even when called. The cookie + super-properties
 * still work, satisfying the Phase 113 stitch invariant for the Wave-0 tests.
 */
export function emit<E extends EventName>(event: E, props: EventProperties<E>): void {
  if (typeof window === 'undefined') return;
  if (!initialized) return;
  // In local-dev, api_host is unreachable so the network send fails silently;
  // we still call posthog.capture so posthog.on('eventCaptured') fires for
  // Playwright test assertions.
  posthog.capture(event, props);
}
